type LegResult = {
  winner: string;
  margin: number;
  amount: number;
};

type NassauResult = {
  front: LegResult;
  back: LegResult;
  overall: LegResult;
};

type Press = {
  pressNumber: number;
  type: "OnDemand" | "TwoDownAuto";
  startingHole: number;
  initiatedBy: string | null;
  winner: string | null;
  amount: number;
  betType?: string;
};

type Props = {
  data: NassauResult;
  presses: Press[];
  hideMoney?: boolean;
};

function formatMoney(n: number): string {
  if (!Number.isFinite(n)) return "$0";
  const abs = Math.abs(n);
  return `$${abs % 1 === 0 ? abs.toFixed(0) : abs.toFixed(2)}`;
}

function LegColumn({
  label,
  leg,
  hideMoney,
  testId,
}: {
  label: string;
  leg: LegResult;
  hideMoney?: boolean;
  testId: string;
}) {
  const tied = leg.winner === "Tied" || leg.margin === 0;
  return (
    <div className="text-center" data-testid={testId}>
      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">{label}</p>
      <p className="text-sm font-bold text-gray-900 truncate" data-testid={`${testId}-winner`}>
        {tied ? "Tied" : leg.winner}
      </p>
      <p className="text-xs text-gray-500 mt-0.5" data-testid={`${testId}-margin`}>
        {tied ? "Tied" : `+${leg.margin} bet${leg.margin === 1 ? "" : "s"}`}
      </p>
      {!hideMoney && (
        <p
          className={`text-base font-bold mt-1 ${
            leg.amount > 0
              ? "text-emerald-600"
              : "text-gray-400"
          }`}
          data-testid={`${testId}-amount`}
        >
          {leg.amount > 0 ? formatMoney(leg.amount) : "—"}
        </p>
      )}
    </div>
  );
}

export function NassauResultCard({ data, presses, hideMoney }: Props) {
  return (
    <div className="space-y-3" data-testid="card-nassau-result">
      <div className="bg-white rounded-xl border border-gray-100 p-4">
        <div className="grid grid-cols-3 gap-3">
          <LegColumn label="Front" leg={data.front} hideMoney={hideMoney} testId="leg-front" />
          <LegColumn label="Back" leg={data.back} hideMoney={hideMoney} testId="leg-back" />
          <LegColumn label="Match" leg={data.overall} hideMoney={hideMoney} testId="leg-overall" />
        </div>
      </div>

      {presses.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-100 p-4" data-testid="press-timeline">
          <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">Presses</p>
          <ul className="space-y-2">
            {presses.map((p) => (
              <li
                key={`press-${p.pressNumber}`}
                className="flex items-start justify-between gap-2"
                data-testid={`press-row-${p.pressNumber}`}
              >
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-gray-900">
                    Press {p.pressNumber} · Hole {p.startingHole}
                  </p>
                  <p className="text-xs text-gray-500 truncate">
                    {p.type === "TwoDownAuto"
                      ? "Auto"
                      : p.initiatedBy
                      ? `${p.initiatedBy} called it`
                      : "Manual press"}
                  </p>
                </div>
                <div className="text-right shrink-0">
                  {p.winner ? (
                    <>
                      <p className="text-sm font-bold text-gray-900 truncate max-w-[120px]">
                        {p.winner}
                      </p>
                      {!hideMoney && (
                        <p className="text-xs font-bold text-emerald-600">
                          +{formatMoney(p.amount)}
                        </p>
                      )}
                    </>
                  ) : (
                    <p className="text-xs text-gray-400">Tied</p>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
